import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "~/util/cn";

type VerticalStepsScrollProps<T> = {
  options: T[];
  onChange: (item: T, index: number) => void;
  children: (item: T, isCenter: boolean) => JSX.Element;
  itemHeight: number;
  itemsShown: number;
  gap?: number;
  className?: string;
};

export const VerticalSteppedScroller = <T,>({
  options,
  onChange,
  children,
  itemHeight,
  itemsShown: rawItemsShown,
  gap = 0,
  className,
}: VerticalStepsScrollProps<T>): JSX.Element => {
  const itemsShown = Math.max(3, rawItemsShown);

  const ref = useRef<HTMLDivElement | null>(null);
  const [centerIndex, setCenterIndex] = useState<number>(0);
  const containerHeight = itemHeight * itemsShown + gap * (itemsShown - 1);
  const effectiveItemHeight = itemHeight + gap;

  const spacerHeight = itemHeight * Math.floor((itemsShown - 1) / 2);

  const scrollToItem = useCallback(
    (index: number) => {
      if (ref.current) {
        ref.current.scrollTo({
          top: index * effectiveItemHeight,
          behavior: "smooth",
        });
      }
    },
    [effectiveItemHeight],
  );

  useEffect(() => {
    const scrollContainer = ref.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const centerPosition = scrollTop + containerHeight / 2;
      const newCenterIndex = Math.max(
        0,
        Math.min(
          Math.floor((centerPosition - spacerHeight) / effectiveItemHeight),
          options.length - 1,
        ),
      );

      if (newCenterIndex !== centerIndex) {
        setCenterIndex(newCenterIndex);
        onChange(options[newCenterIndex], newCenterIndex);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [
    containerHeight,
    itemHeight,
    effectiveItemHeight,
    spacerHeight,
    options,
    centerIndex,
    onChange,
  ]);

  useEffect(() => {
    scrollToItem(0);
  }, [scrollToItem]);

  return (
    <div className="fade-y-base-100 rounded-box bg-base-100">
      <div
        ref={ref}
        className={cn("snap-y snap-mandatory overflow-y-scroll", className)}
        style={{ height: containerHeight }}
      >
        <div className="w-full" style={{ height: spacerHeight }} />
        {options.map((item, index) => (
          <div
            key={index}
            className="snap-center"
            style={{
              height: itemHeight,
              marginBottom: index < options.length - 1 ? gap : 0,
            }}
            onClick={() => {
              scrollToItem(index);
              setCenterIndex(index);
              onChange(item, index);
            }}
          >
            {children(item, index === centerIndex)}
          </div>
        ))}
        <div className="w-full" style={{ height: spacerHeight }} />
      </div>
    </div>
  );
};
